import {BackendResponse} from './backend-response';

export const defaultPerPage = 15;
export const perPageOptions = [
  {key: 15},
  {key: 30},
  {key: 50},
  {key: 100},
  {key: 200},
];

export type SimplePaginationMeta = {
  from: number | null;
  to: number | null;
  per_page: number;
  current_page: number;
  next_page?: number | null;
  prev_page?: number | null;
};

export type LengthAwarePaginationMeta = SimplePaginationMeta & {
  total: number;
  last_page: number;
};

export type CursorPaginationMeta = {
  next_cursor: string | null;
  prev_cursor: string | null;
  per_page: number;
};

export type PaginationMeta =
  SimplePaginationMeta | LengthAwarePaginationMeta | CursorPaginationMeta;

export type PaginationLinks = {
  first: string | null;
  last: string | null;
  prev: string | null;
  next: string | null;
};

export type PaginatedResource<T = unknown> = {
  data: T[];
  meta: PaginationMeta;
  links: PaginationLinks;
};

export function hasPreviousPage(
  meta: PaginationMeta | PaginatedBackendResponse<any> | PaginatedResource<any>,
): boolean {
  if ('meta' in meta) {
    meta = meta.meta;
  }

  if ('pagination' in meta) {
    meta = meta.pagination;
  }

  if ('prev_cursor' in meta) {
    return meta.prev_cursor != null;
  }

  if ('prev_page' in meta) {
    return meta.prev_page != null;
  }

  if ('prev_page_url' in meta) {
    return meta.prev_page_url != null;
  }

  return meta.current_page > 1;
}

export function hasNextPage(
  meta: PaginationMeta | PaginatedBackendResponse<any> | PaginatedResource<any>,
): boolean {
  if ('links' in meta && meta.links && 'next' in meta.links && meta.links.next) {
    return true;
  }

  if ('meta' in meta) {
    meta = meta.meta;
  }

  if ('pagination' in meta) {
    meta = meta.pagination;
  }

  if ('next_cursor' in meta) {
    return meta.next_cursor != null;
  }

  if ('last_page' in meta) {
    return meta.current_page < (meta as LengthAwarePaginationMeta).last_page;
  }

  if ('next_page' in meta) {
    return meta.next_page != null;
  }

  if ('next_page_url' in meta) {
    return meta.next_page_url != null;
  }

  return false;
}

export function getPreviousPageParam(
  response: PaginatedResource | PaginatedBackendResponse<any>,
): number | string | null {
  if ('meta' in response) {
    if ('prev_cursor' in response.meta) {
      return response.meta.prev_cursor;
    }

    return response.meta.current_page - 1;
  }

  if ('pagination' in response) {
    if ('prev_cursor' in response.pagination) {
      return response.pagination.prev_cursor;
    }

    return response.pagination.current_page - 1;
  }

  return null;
}

export function getNextPageParam(
  response: PaginatedResource | PaginatedBackendResponse<any>,
): number | string | null {
  if ('meta' in response) {
    if ('next_cursor' in response.meta) {
      return response.meta.next_cursor;
    }

    return response.meta.current_page + 1;
  }

  if ('pagination' in response) {
    if ('next_cursor' in response.pagination) {
      return response.pagination.next_cursor;
    }

    return response.pagination.current_page + 1;
  }

  return null;
}

export function getTotal(
  response: Omit<PaginatedResource, 'data'> | PaginatedBackendResponse<any>,
): number | undefined {
  if (
    'meta' in response &&
    'total' in response.meta &&
    response.meta.total != null
  ) {
    return response.meta.total;
  }

  if (
    'pagination' in response &&
    'total' in response.pagination &&
    response.pagination.total != null
  ) {
    return response.pagination.total;
  }

  return undefined;
}

export function getPerPage(
  response: Omit<PaginatedResource, 'data'> | PaginatedBackendResponse<any>,
): number | undefined {
  if ('meta' in response) {
    return response.meta.per_page;
  }

  if ('pagination' in response) {
    return response.pagination.per_page;
  }

  return undefined;
}

// legacy, use PaginatedResource and PaginationMeta instead
export type LengthAwarePaginationResponse<T = unknown> =
  LengthAwarePaginationMeta & {
    data: T[];
  };

export type SimplePaginationResponse<T = unknown> = SimplePaginationMeta & {
  data: T[];
};

export type CursorPaginationResponse<T> = CursorPaginationMeta & {
  data: T[];
};

export type PaginationResponse<T> =
  | LengthAwarePaginationResponse<T>
  | SimplePaginationResponse<T>
  | CursorPaginationResponse<T>;

export interface PaginatedBackendResponse<T> extends BackendResponse {
  pagination: PaginationResponse<T>;
}

export const EMPTY_PAGINATION_RESPONSE = {
  pagination: {data: [], from: 0, to: 0, per_page: 15, current_page: 1},
};
